<template>
  <div
    id="calenderListingContainer"
    class="max-w-screen-lg mx-auto">
    <svg
      v-if="isLoading"
      class="w-8 h-8 mx-auto animate-spin text-red"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24">
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"></circle>
      <path
        class="opacity-95"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <div
      class="mt-4"
      v-if="!isLoading">
      <div class="">
        <div
          v-if="showFilters"
          class="flex flex-wrap lg:flex-nowrap justify-center gap-4"
          v-auto-animate>
          <button
            @click="filterEventsByCategory(category.name)"
            v-for="(category, idx) in categories"
            :key="idx"
            :style="[
              activeCategory === category.name && {
                backgroundColor: 'white',
                color: 'var(--text-red)',
              },
            ]"
            class=" btn-rounded">
            {{ category.name }}
          </button>
        </div>
        <div
          v-if="showCurrentMonth"
          class="mt-10">
          <div class="flex justify-between items-center mt-10">
            <button
              @click="previousMonth"
              v-if="!isPreviousMonthDisabled">
              <svg
                class="w-12 h-12 text-red"
                style="transform: scaleX(-1)"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 140 64.17"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="5.83px"
                  d="M107.92 2.91l29.16 29.17-29.16 29.17M2.92 32.08h134.16"></path>
              </svg>
            </button>
            <div
              v-else
              class="w-12 h-12"></div>
            <div
              class="uppercase title font-oswald"
              v-html="currentMonthDisplay" />
            <button @click="nextMonth">
              <svg
                class="w-12 h-12 text-red"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 140 64.17"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="5.83px"
                  d="M107.92 2.91l29.16 29.17-29.16 29.17M2.92 32.08h134.16"></path>
              </svg>
            </button>
          </div>
        </div>
        <div
          class="mt-8"
          v-auto-animate="{ easing: 'ease-in' }">
          <div
            v-for="(event, index) in monthFilteredEvents"
            :key="`${event.description}-${event.category.join('-')}-${index}`"
            class="py-6 border-b border-gray flex flex-col gap-4 lg:gap-6 relative">
            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
              <div class="flex flex-col items-start gap-2 text-left">
                <div class="font-mono text-gray text-sm flex flex-wrap items-center gap-2">
                  <span>// {{ getFormattedEventDates(event) }}</span>
                  <span
                    v-if="getFormattedEventTime(event)"
                    class="opacity-60">
                    // {{ getFormattedEventTime(event) }}
                  </span>
                </div>
                <h2 class="font-oswald text-2xl lg:text-3xl">
                  {{ event.description }}
                </h2>
              </div>
              <div class="flex items-center gap-4 flex-shrink-0">
                <a
                  :href="`#calendar-event-${index}`"
                  class="btn-primary btn-rounded calendar-lightbox-trigger"
                  data-glightbox="type: inline">
                  <span class="absolute inset-0 z-10" />
                  Weitere Informationen
                </a>
              </div>
            </div>
            <div
              :id="`calendar-event-${index}`"
              class="calendar-event-modal-content glightbox-calendar"
              style="display: none"
              aria-hidden="true">
              <div class="space-y-5 p-4">
                <div class="uppercase text-red text-sm font-semibold">
                  {{ event.category.join(', ') }}
                </div>
                <h2 class="font-oswald text-2xl lg:text-3xl">
                  {{ event.description }}
                </h2>
                <div class="font-mono text-gray text-sm flex flex-wrap items-center gap-2">
                  <span>{{ getFormattedEventDates(event) }}</span>
                  <span
                    v-if="getFormattedEventTime(event)"
                    class="opacity-60">
                    {{ getFormattedEventTime(event) }}
                  </span>
                </div>
                <div
                  class="event-description"
                  v-html="event.longdescription"></div>
                <div
                  v-if="event.sponsor && event.sponsor.length"
                  class="pt-4">
                  <h3 class="text-sm font-semibold mb-2">{{ event.sponsorTitle }}</h3>
                  <div class="flex flex-wrap items-center gap-4">
                    <img
                      v-for="(url, idx) in event.sponsor"
                      :key="`sponsor-${idx}`"
                      :src="resolveImageUrl(url)"
                      :alt="`Sponsor ${idx + 1}`"
                      style="max-width: 200px"
                      class="h-auto"
                      loading="lazy" />
                  </div>
                </div>
                <div
                  v-if="event.supporters && event.supporters.length"
                  class="pt-4">
                  <h3 class="text-sm font-semibold mb-2">{{ event.supportersTitle }}</h3>
                  <div class="flex flex-wrap items-center gap-4">
                    <img
                      v-for="(url, idx) in event.supporters"
                      :key="`supporter-${idx}`"
                      :src="resolveImageUrl(url)"
                      :alt="`Unterstützer ${idx + 1}`"
                      style="max-width: 200px"
                      class="h-auto"
                      loading="lazy" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <a
          v-if="showAllButton"
          href="https://www.museumpab.de/informatives/kalender/"
          class="btn-primary btn-rounded mt-6">
          Alle Veranstaltungen anzeigen
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import GLightbox from 'glightbox';
import { uniqBy } from 'lodash-es';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths
} from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { de } from 'date-fns/locale';

const API_URL = '/middelware/findDaysForMonthGrouped';
const EVENT_TIMEZONE = 'Europe/Berlin';

export default {
  name: 'CalendarListing',
  props: {
    showCurrentMonth: {
      type: Boolean,
      default: true
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: null
    },
    showPastEvents: {
      type: Boolean,
      default: true
    },
    showAllButton: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    events: [],
    activeCategory: '',
    isLoading: true,
    currentMonth: TZDate.tz(EVENT_TIMEZONE),
    calendarLightboxInstance: null
  }),
  async mounted () {
    await this.getEvents();
  },
  // Vue 3: beforeDestroy → beforeUnmount
  beforeUnmount () {
    if (this.calendarLightboxInstance) {
      this.calendarLightboxInstance.destroy();
      this.calendarLightboxInstance = null;
    }
  },
  watch: {
    monthFilteredEvents: {
      handler () {
        this.$nextTick(() => {
          this.initCalendarLightbox();
        });
      },
      immediate: true
    }
  },
  computed: {
    categories () {
      const monthStart = startOfMonth(this.currentMonth).getTime() / 1000;
      const monthEnd = endOfMonth(this.currentMonth).getTime() / 1000;
      const now = new TZDate(Date.now(), EVENT_TIMEZONE);

      const eventsInMonth = this.events.filter((event) => {
        if (!event.dates || !Array.isArray(event.dates)) return false;

        // Filter dates based on showPastEvents prop
        const relevantDates = this.showPastEvents
          ? event.dates
          : event.dates.filter(date => date >= now);

        // Check if event has dates in the current month
        return relevantDates.some((eventDate) => {
          const eventTimestamp = eventDate.getTime() / 1000;
          return eventTimestamp >= monthStart && eventTimestamp <= monthEnd;
        });
      });

      // Extract all categories from events
      const allCategories = [];
      eventsInMonth.forEach((event) => {
        allCategories.push(...event.category);
      });

      return uniqBy(
        allCategories.map((category) => ({
          name: category
        })),
        'name'
      );
    },
    currentMonthDisplay () {
      return (
        format(this.currentMonth, 'MMMM', { locale: de }) +
        format(this.currentMonth, "'<sup>'yy'</sup>'")
      );
    },
    isPreviousMonthDisabled () {
      const now = new TZDate(Date.now(), EVENT_TIMEZONE);
      return startOfMonth(this.currentMonth) <= startOfMonth(now);
    },
    monthFilteredEvents () {
      const monthStart = startOfMonth(this.currentMonth);
      const monthEnd = endOfMonth(this.currentMonth);
      const now = new TZDate(Date.now(), EVENT_TIMEZONE);

      // First, filter out past dates from each event if showPastEvents is false
      let processedEvents = this.events.map(event => {
        if (!event.dates || !Array.isArray(event.dates)) return event;

        const filteredDates = this.showPastEvents
          ? event.dates
          : event.dates.filter(date => date >= now);

        return {
          ...event,
          dates: filteredDates
        };
      });

      // Filter out events that have no dates left after filtering
      if (!this.showPastEvents) {
        processedEvents = processedEvents.filter(event =>
          event.dates && event.dates.length > 0
        );
      }

      // Filter events to only those with dates in the current month
      const eventsInMonth = processedEvents.filter((event) => {
        if (!event.dates || !Array.isArray(event.dates)) return false;

        return event.dates.some((eventDate) => {
          return eventDate >= monthStart && eventDate <= monthEnd;
        });
      });

      // Sort events by their first date, same day by start time
      const sortedEvents = eventsInMonth.sort((a, b) => {
        const aFirstDate =
          a.dates && a.dates.length > 0 ? a.dates[0] : new Date(0);
        const bFirstDate =
          b.dates && b.dates.length > 0 ? b.dates[0] : new Date(0);
        const byDate = aFirstDate - bFirstDate;
        if (byDate !== 0) return byDate;
        // Ohne Uhrzeit (ganztägig) zuerst
        const aTime = a.time ? a.time.getTime() : 0;
        const bTime = b.time ? b.time.getTime() : 0;
        return aTime - bTime;
      });

      // Apply category filter if active
      let finalEvents = sortedEvents;
      if (this.activeCategory) {
        finalEvents = sortedEvents.filter((event) =>
          event.category.includes(this.activeCategory)
        );
      }

      // Apply limit if specified
      if (this.limit && this.limit > 0) {
        finalEvents = finalEvents.slice(0, this.limit);
      }

      return finalEvents;
    }
  },
  methods: {
    initCalendarLightbox () {
      if (this.calendarLightboxInstance) {
        this.calendarLightboxInstance.destroy();
        this.calendarLightboxInstance = null;
      }

      this.calendarLightboxInstance = GLightbox({
        selector: '#calenderListingContainer .calendar-lightbox-trigger',
        skin: 'clean',
        width: 650,
        height: 'auto',
        draggable: false,
        zoomable: false,
        keyboardNavigation: false,
        touchFollowAxis: false,
        touchNavigation: false
      });
    },
    filterEventsByCategory (categoryName) {
      if (this.activeCategory === categoryName) {
        this.resetFilter();
      } else {
        this.activeCategory = categoryName;
      }
    },
    resetFilter () {
      this.activeCategory = '';
    },
    previousMonth () {
      const shifted = subMonths(this.currentMonth, 1);
      this.currentMonth = new TZDate(shifted.getTime(), EVENT_TIMEZONE);
      this.resetFilter();
    },
    nextMonth () {
      const shifted = addMonths(this.currentMonth, 1);
      this.currentMonth = new TZDate(shifted.getTime(), EVENT_TIMEZONE);
      this.resetFilter();
    },
    getFormattedEventDates (event) {
      if (
        !event.dates ||
        !Array.isArray(event.dates) ||
        event.dates.length === 0
      ) {
        return '';
      }

      const listFormatter = new Intl.ListFormat('de', {
        style: 'long',
        type: 'conjunction'
      });

      return listFormatter.format(
        event.dates.map((date) =>
          // Wochentag kurz ohne Punkt + volles Jahr, z. B. "Do 29.10.2026"
          format(date, 'EEEEEE dd.MM.yyyy', { locale: de })
        )
      );
    },
    getFormattedEventTime (event) {
      if (!event || !event.time) {
        return '';
      }

      const formattedTime = format(event.time, 'HH:mm');

      if (!formattedTime) {
        return '';
      }

      if (event.endtime) {
        const formattedEndTime = format(event.endtime, 'HH:mm');
        return `${formattedTime}–${formattedEndTime} Uhr`;
      }

      return `${formattedTime} Uhr`;
    },
    // Relativen Dateipfad auf absoluten URL bringen (Fallback falls kein führender Slash)
    resolveImageUrl (url) {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      if (!url.startsWith('/')) url = '/' + url;
      return window.location.origin + url;
    },
    async getEvents () {
      this.isLoading = true;
      try {
        const { data } = await axios.get(API_URL);
        this.events = data.map((event) => ({
          ...event,
          dates: event.dates
            ? event.dates.map((timestamp) => new TZDate(timestamp * 1000, EVENT_TIMEZONE))
            : [],
          time: event.time ? new TZDate(event.time * 1000, EVENT_TIMEZONE) : null,
          endtime: event.endtime ? new TZDate(event.endtime * 1000, EVENT_TIMEZONE) : null
        }));

        this.isLoading = false;
      } catch (_err) {
        this.isLoading = false;
      }
    }
  }
};
</script>
